import { useEffect } from 'react'

/**
 * What each admin screen last showed, kept for the rest of the session, so a screen or tab the
 * photographer has already opened comes back at once instead of behind its skeleton.
 *
 * Stale while revalidate: a screen that finds an entry renders it straight away and still sends
 * the same request it always did, then replaces the entry's rows with the answer. Nothing here
 * decides what is true; it only decides what is on screen while the answer is on its way. The
 * first visit to a screen is unchanged and shows its skeleton.
 *
 * - **In memory only.** Never written to storage, so a reload starts clean and nothing reaches
 *   disk. A full page load (including the one after logout) discards it.
 * - **Scoped to the account's token,** so a different login in the same tab can never read
 *   another account's rows, and cleared on logout as well (see the `logout` reducer branch).
 * - **One entry per screen, not per query.** Screens write only the view a fresh visit starts
 *   on (the first page, no search, the default sort), because that is the only view a revisit
 *   can show. A later page or search is never what a revisit opens on.
 */
const entries = new Map()

const entryKey = (token, key) => `${token}\u0000${key}`

/** The entry `key` holds for this account, or `undefined`. */
export const readScreenCache = (token, key) => (token ? entries.get(entryKey(token, key)) : undefined)

/** Replaces the entry. A missing token writes nothing, so a signed-out render cannot seed one. */
export const writeScreenCache = (token, key, value) => {
  if (!token || value === undefined) return
  entries.set(entryKey(token, key), value)
}

/** Forgets everything, for every account. */
export const clearScreenCache = () => entries.clear()

/**
 * Keeps an entry in step with a screen's own state: whenever `value` changes while `ready` is
 * true, it is written. That is what makes an in-place change (a delete, a duplicate, a modal's
 * save) the thing the next visit starts from, without every call site remembering to write.
 * Pass `ready = false` while the state is not something a revisit should show (before the first
 * load, or while it holds a page other than the first).
 */
export const useScreenCacheWrite = (token, key, value, ready = true) => {
  useEffect(() => {
    if (ready) writeScreenCache(token, key, value)
  }, [token, key, value, ready])
}
